import { useNavigate } from 'react-router-dom';
import { show as IntercomShow } from '@intercom/messenger-js-sdk';
import { useState, useRef } from 'react';
import { ROUTE_LABS_ORDER_STEP_1 } from '../../../constants/routes';
import DashboardNavigationBar from '../../../components/dashboard/navigation-bar/dashboard-navigation-bar';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import Resources from '@/components/resources/resources';
import {
  useListLabOrdersV1LabsOrdersGet,
  useGetLabOrderV1LabsOrdersOrderIdGet,
} from '../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { TopLevelOrderStatus } from '../../../api/generated-catch-backend/model';
import { BiomarkerDashboard } from './components/biomarker-dashboard';
import {
  LabsHeaderNoOrder,
  LabsHeaderOrdered,
  LabsHeaderError,
  LabsHeaderBiomarkers,
  type OrderInfo,
} from './components/labs-headers';
import { ScheduleVisitModal } from './components/schedule-visit-modal';
import { getLabProviderInfo } from './utils/lab-provider-mapping';
import { formatPatientAddress } from './utils/format-address';
import { FeatureFlags, useFeatureFlags } from '@/services/feature-flag-provider';
import { useDownloadLabOrder } from '../labs-order/hooks/useDownloadLabOrder';

export function Page() {
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const isCatchLabsEnabled = isEnabled(FeatureFlags.IsCatchLabsEnabled);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { downloadOrder } = useDownloadLabOrder();

  // Fetch user's lab orders
  const { data: ordersData, isLoading: isLoadingOrders } = useListLabOrdersV1LabsOrdersGet();
  const orders = ordersData?.data || [];
  const latestOrder = orders[0];

  // Fetch detailed information for the most recent order
  const { data: orderDetail, isLoading: isLoadingOrderDetail } = useGetLabOrderV1LabsOrdersOrderIdGet(
    latestOrder?.orderId || '',
    {
      query: {
        enabled: !!latestOrder?.orderId,
      },
    },
  );

  const topLevelStatus = ordersData?.meta.status;

  if (!isCatchLabsEnabled) {
    return (
      <>
        <DashboardNavigationBar activeItem="labs" />
        <DashboardLayout>
          <DashboardGrid>
            <div className="rounded-lg bg-white p-8 text-center shadow-md">
              <div className="mb-6 text-6xl">
                <span role="img" aria-label="rocket">
                  🚀
                </span>
              </div>
              <h1 className="mb-4 text-3xl font-bold text-gray-900">This functionality is coming soon!</h1>
              <p className="text-gray-600">
                We're working hard to bring you something amazing.
                <br />
                Check back later for updates.
              </p>
            </div>
            <Resources as="aside" width="full" />
          </DashboardGrid>
        </DashboardLayout>
      </>
    );
  }

  // Get lab provider info from order detail
  // V1 response has junctionProviderId as string, but getLabProviderInfo expects number
  const labProviderId = orderDetail?.junctionProviderId ? parseInt(orderDetail.junctionProviderId, 10) : undefined;
  const labProviderInfo = getLabProviderInfo(labProviderId);

  // Construct order info from actual order data
  const orderInfo: OrderInfo = {
    orderCode: orderDetail?.orderId || latestOrder?.orderId || 'N/A',
    labName: labProviderInfo?.name || 'Quest Diagnostics', // Fallback to Quest if not found
    address: formatPatientAddress(
      orderDetail
        ? {
            line1: orderDetail.addressLine1,
            city: orderDetail.city,
            state: orderDetail.state,
            postalCode: orderDetail.postalCode,
          }
        : undefined,
    ),
    // Note: Distance is omitted here - will be added when backend provides PSC distance data
  };

  const handleOrderClick = () => {
    navigate(ROUTE_LABS_ORDER_STEP_1);
  };

  const handleContactSupport = () => {
    IntercomShow();
  };

  const handleTryAgain = () => {
    navigate(ROUTE_LABS_ORDER_STEP_1);
  };

  const handleDownloadOrder = () => {
    if (orderDetail?.orderId) {
      downloadOrder(orderDetail.orderId);
    }
  };

  const handleScheduleVisit = () => {
    setIsScheduleModalOpen(true);
  };

  const handleScheduleLabVisit = () => {
    const schedulingUrl =
      labProviderInfo?.schedulingUrl ||
      'https://appointment.questdiagnostics.com/schedule-appointment/as-personal-information';
    window.open(schedulingUrl, '_blank');
    setIsScheduleModalOpen(false);
  };

  const handleGetDirections = () => {
    const address = orderInfo.address;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const handleScrollToCategory = (categorySlug: string) => {
    const targetElement = categoryRefs.current[categorySlug];
    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - 80; // 80px offset for better visibility

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <DashboardNavigationBar activeItem="labs" />

      {/* Render appropriate header based on status */}
      {topLevelStatus === TopLevelOrderStatus.eligible && <LabsHeaderNoOrder onOrderClick={handleOrderClick} />}
      {topLevelStatus === TopLevelOrderStatus.ready_for_testing && (
        <LabsHeaderOrdered
          orderInfo={orderInfo}
          onDownloadOrder={handleDownloadOrder}
          onScheduleVisit={handleScheduleVisit}
          onGetDirections={handleGetDirections}
        />
      )}
      {/* Note: pending_review state is not shown in UI because Junction doesn't send webhooks
          when labs are drawn, only when results are available. Backend may return pending_review
          for COMPLETED status (results received, being processed), but we skip showing a special
          header for this brief intermediate state. */}
      {topLevelStatus === TopLevelOrderStatus.results_ready && (
        <LabsHeaderBiomarkers latestOrder={latestOrder} onCategoryClick={handleScrollToCategory} />
      )}
      {!topLevelStatus && <LabsHeaderError onTryAgain={handleTryAgain} onContactSupport={handleContactSupport} />}

      {/* Schedule Visit Modal */}
      <ScheduleVisitModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSchedule={handleScheduleLabVisit}
        labName={orderInfo.labName}
      />

      <DashboardLayout>
        <DashboardGrid>
          <BiomarkerDashboard categoryRefs={categoryRefs} />
          <Resources as="aside" width="full" />
        </DashboardGrid>
      </DashboardLayout>
    </>
  );
}
